import type { PayloadType } from '../store/useAppStore';

export interface DecodedPayload {
  type: PayloadType;
  data: Record<string, string>;
}

export function parsePayload(payload: string): DecodedPayload {
  const str = payload.trim();
  
  // 1. URL
  if (/^https?:\/\//i.test(str)) {
    return { type: 'url', data: { url: str } };
  }
  
  // 2. Email
  if (/^mailto:/i.test(str)) {
    const emailMatch = /^mailto:([^?]+)/i.exec(str);
    return {
      type: 'email',
      data: { email: emailMatch ? emailMatch[1] : '' }
    };
  }
  if (/^MATMSG:/i.test(str)) {
    const toMatch = /TO:([^;]+);/i.exec(str);
    const subMatch = /SUB:([^;]+);/i.exec(str);
    const bodyMatch = /BODY:([^;]+);/i.exec(str);
    return {
      type: 'email',
      data: {
        email: toMatch ? toMatch[1] : '',
        subject: subMatch ? subMatch[1] : '',
        body: bodyMatch ? bodyMatch[1] : ''
      }
    };
  }
  
  // 3. Phone
  if (/^tel:/i.test(str)) {
    return { type: 'phone', data: { phone: str.substring(4) } };
  }
  
  // 4. SMS
  if (/^smsto:/i.test(str)) {
    const parts = str.substring(6).split(':');
    return { type: 'sms', data: { phone: parts[0] || '', body: parts.slice(1).join(':') || '' } };
  }
  if (/^sms:/i.test(str)) {
    const phoneMatch = /^sms:([^?]+)/i.exec(str);
    const bodyMatch = /[?&]body=([^&]+)/i.exec(str);
    return {
      type: 'sms',
      data: {
        phone: phoneMatch ? phoneMatch[1] : '',
        body: bodyMatch ? decodeURIComponent(bodyMatch[1]) : ''
      }
    };
  }
  
  // 5. WIFI
  if (/^WIFI:/i.test(str)) {
    const ssidMatch = /S:([^;]+);/i.exec(str);
    const passMatch = /P:([^;]+);/i.exec(str);
    const typeMatch = /T:([^;]+);/i.exec(str);
    const hiddenMatch = /H:(true|false);/i.exec(str);
    
    return {
      type: 'wifi',
      data: {
        ssid: ssidMatch ? ssidMatch[1] : '',
        password: passMatch ? passMatch[1] : '',
        security: typeMatch ? typeMatch[1] : 'WPA',
        hidden: hiddenMatch ? hiddenMatch[1].toLowerCase() : 'false'
      }
    };
  }
  
  // 6. VCard
  if (/^BEGIN:VCARD/i.test(str)) {
    const extract = (regex: RegExp) => {
      const match = str.match(regex);
      return match ? match[1].trim() : '';
    };
    const fn = extract(/FN:([^\n\r]+)/i);
    const phone = extract(/TEL[^:]*:([^\n\r]+)/i);
    const email = extract(/EMAIL[^:]*:([^\n\r]+)/i);
    const org = extract(/ORG:([^\n\r]+)/i);
    const title = extract(/TITLE:([^\n\r]+)/i);
    const url = extract(/URL:([^\n\r]+)/i);
    const note = extract(/NOTE:([^\n\r]+)/i);
    
    const adr = extract(/ADR[^:]*:([^\n\r]+)/i);
    let street = '', city = '', country = '';
    if (adr) {
      const parts = adr.split(';');
      street = parts[2] || '';
      city = parts[3] || '';
      country = parts[6] || '';
    }
    
    return {
      type: 'vcard',
      data: { fn, phone, email, org, title, url, note, street, city, country }
    };
  }
  
  // MECARD
  if (/^MECARD:/i.test(str)) {
    const extract = (regex: RegExp) => {
      const match = str.match(regex);
      return match ? match[1].trim() : '';
    };
    return {
      type: 'vcard',
      data: {
        fn: extract(/N:([^;]+);/i),
        phone: extract(/TEL:([^;]+);/i),
        email: extract(/EMAIL:([^;]+);/i),
        url: extract(/URL:([^;]+);/i),
        note: extract(/NOTE:([^;]+);/i),
        street: extract(/ADR:([^;]+);/i),
        org: '', title: '', city: '', country: ''
      }
    };
  }
  
  // 7. Geo
  if (/^geo:/i.test(str)) {
    const match = /^geo:([-0-9.]+),([-0-9.]+)/i.exec(str);
    if (match) {
      return { type: 'geo', data: { lat: match[1], lng: match[2] } };
    }
  }
  
  // 8. Calendar
  if (/^BEGIN:VEVENT/i.test(str)) {
    const extract = (regex: RegExp) => {
      const match = str.match(regex);
      return match ? match[1].trim() : '';
    };
    const formatTime = (timeStr: string) => {
      if (!timeStr) return '';
      const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/.exec(timeStr);
      if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
      return '';
    };
    
    return {
      type: 'calendar',
      data: {
        title: extract(/SUMMARY:([^\n\r]+)/i),
        start: formatTime(extract(/DTSTART[^:]*:([^\n\r]+)/i)),
        end: formatTime(extract(/DTEND[^:]*:([^\n\r]+)/i)),
        location: extract(/LOCATION:([^\n\r]+)/i),
        description: extract(/DESCRIPTION:([^\n\r]+)/i)
      }
    };
  }
  
  // Fallback to plain text for all non-standard or unidentified data
  return { type: 'text', data: { text: payload } };
}
